import React, { useEffect, useState } from 'react';
import { Plus, Copy, Trash2, Pencil, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { PostCallPromptEditor } from './PostCallPromptEditor';
import { copyPlainText } from '../utils/clipboard';
import { parsePromptLibrary } from '../utils/promptLibrary';

const blankLibrary = { prompts: [], default_prompt: '' };
export const promptSaveError = (err) => typeof err?.response?.data?.detail === 'string' ? err.response.data.detail : 'Could not save the prompt. Your changes have been kept.';
export const copyPromptId = async (id) => {
  try { await copyPlainText(id); toast.success('Prompt ID copied'); }
  catch { toast.error('Clipboard unavailable. Select the prompt ID and copy it manually.'); }
};

export const PostCallPromptLibrary = ({ api, subAccountId }) => {
  const [library, setLibrary] = useState(blankLibrary);
  const [starters, setStarters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [revision, setRevision] = useState(0);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState('');
  const [query, setQuery] = useState('');
  const base = `/api/sub-accounts/${subAccountId}/post-call-sms`;

  useEffect(() => {
    let active = true;
    setLoading(true); setError(''); setEditing(null); setLibrary(blankLibrary); setStarters([]);
    api.get(`${base}/prompts`).then(({ data }) => { if (active) setLibrary(parsePromptLibrary(data)); })
      .catch(() => { if (active) setError('Saved prompts could not be loaded.'); })
      .finally(() => { if (active) setLoading(false); });
    // Optional starter presets must never lock access to a saved custom prompt.
    api.get('/api/post-call-sms/templates').then(({ data }) => {
      if (active) setStarters(Array.isArray(data?.templates) ? data.templates.filter(t =>
        t && typeof t.id === 'string' && typeof t.label === 'string' && typeof t.prompt === 'string') : []);
    }).catch(() => {});
    return () => { active = false; };
  }, [api, base, revision]);

  const onSaved = (data, isDefault) => {
    setLibrary(prev => isDefault ? { ...prev, default_prompt: data.prompt } :
      { ...prev, prompts: [...prev.prompts.filter(p => p.prompt_id !== data.prompt_id), data] });
    setEditing(null); setQuery(''); toast.success('Prompt saved');
  };
  const remove = async (row) => {
    if (!window.confirm(`Delete “${row.name}”? Workflows using this ID must be updated before they can send SMS.`)) return;
    setDeleting(row.prompt_id);
    try {
      await api.delete(`${base}/prompts/${row.prompt_id}`);
      setLibrary(prev => ({ ...prev, prompts: prev.prompts.filter(p => p.prompt_id !== row.prompt_id) }));
      toast.success('Prompt deleted');
    } catch (err) { toast.error(promptSaveError(err)); }
    finally { setDeleting(''); }
  };
  const rows = library.prompts.filter(p => `${p.name} ${p.prompt_id}`.toLowerCase().includes(query.toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name));

  return <section className="api-prompt-library" data-testid="post-call-sms-prompt-card">
    <div className="api-prompt-heading"><h3 data-testid="post-call-prompts-heading">Saved SMS prompts <span className="prompt-count" data-testid="post-call-prompts-count">{library.prompts.length}</span></h3>
      <Button type="button" className="prompt-primary" disabled={loading || !!error} onClick={() => setEditing({ prompt_id: 'new', name: '', prompt: '' })} data-testid="post-call-prompt-new"><Plus size={16} />New prompt</Button>
    </div>
    {loading ? <p role="status" data-testid="post-call-prompts-loading">Loading saved prompts…</p> : error ?
      <div className="api-prompt-error" role="alert" data-testid="post-call-prompts-error">{error}<Button type="button" variant="ghost" onClick={() => setRevision(n => n + 1)} data-testid="post-call-prompts-retry">Retry</Button></div> : <>
      <div className="prompt-default-row" data-testid="post-call-prompt-row-default"><div><strong>Default fallback</strong><span>{library.default_prompt ? 'Custom default saved' : 'Agent or built-in default'}</span></div>
        <Button type="button" variant="outline" onClick={() => setEditing({ prompt_id: 'default', name: 'Default fallback', prompt: library.default_prompt })} data-testid="post-call-prompt-edit-default"><Pencil size={14} />Edit default</Button></div>
      {library.prompts.length > 4 && <div className="prompt-search"><Search size={16} /><Input value={query} onChange={e => setQuery(e.target.value)} placeholder="Find a saved prompt" aria-label="Find a saved prompt" data-testid="post-call-prompt-search" /></div>}
      <div className="prompt-list" data-testid="post-call-prompt-list">
        {rows.length ? rows.map(row => <div className="prompt-list-row" key={row.prompt_id} data-testid={`post-call-prompt-row-${row.prompt_id}`}>
          <div className="prompt-list-info"><button type="button" onClick={() => setEditing(row)} data-testid={`post-call-prompt-open-${row.prompt_id}`}>{row.name}</button><code data-testid={`post-call-prompt-id-${row.prompt_id}`}>{row.prompt_id}</code></div>
          <div className="prompt-row-actions"><Button type="button" variant="outline" onClick={() => setEditing(row)} data-testid={`post-call-prompt-edit-${row.prompt_id}`}><Pencil size={14} />Edit</Button>
            <Button type="button" variant="ghost" onClick={() => copyPromptId(row.prompt_id)} data-testid={`post-call-prompt-copy-${row.prompt_id}`} title="Copy prompt_id for Custom Data"><Copy size={14} />Copy ID</Button>
            <Button type="button" variant="ghost" disabled={!!deleting} onClick={() => remove(row)} aria-label={`Delete ${row.name}`} title="Delete prompt" data-testid={`post-call-prompt-delete-${row.prompt_id}`}><Trash2 size={15} /></Button></div>
        </div>) : <p className="prompt-empty" data-testid="post-call-prompts-empty">{query ? 'No matching prompts.' : 'No named prompts yet.'}</p>}
      </div>
    </>}
    {editing && <PostCallPromptEditor key={`${subAccountId}:${editing.prompt_id}`} row={editing} starters={starters} api={api} base={base} onSaved={onSaved} onClose={() => setEditing(null)} />}
  </section>;
};